/**
 * Portuguese (Português) Language File
 * 
 * This file contains all Portuguese translations for the application.
 */

export const pt = {
  // Language info
  language: {
    code: 'pt',
    name: 'Português',
    flag: '🇧🇷',
  },

  // Common/General translations
  common: {
    welcome: 'Bem-vindos',
    back: 'Voltar',
    backTo: 'Voltar para',
    next: 'Próximo',
    previous: 'Anterior',
    continue: 'Continuar',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    save: 'Salvar',
    delete: 'Excluir',
    edit: 'Editar',
    close: 'Fechar',
    loading: 'Carregando',
    error: 'Erro',
    success: 'Sucesso',
    warning: 'Aviso',
    info: 'Informação',
    yes: 'Sim',
    no: 'Não',
    ok: 'OK',
  },

  // Home Page
  home: {
    title: 'Bem-vindos',
    subtitle: 'Escolha um jogo para jogar:',
    chooseGame: 'Escolha um jogo',
    heroSubtitle: 'Jogos interativos que fortalecem a fé e conectam comunidades',
    exploreGames: 'Explorar Jogos',
    ourGames: 'Nossos Jogos',
    conectadosDescription: 'Explore categorias de Sabedoria e Aliança com perguntas projetadas para aprofundar seu conhecimento.',
    playGame: 'Jogar',
    followUs: 'Conecte-se conosco',
    copyright: '© 2025 Fe en Juego. Todos os direitos reservados.',
  },

  // Resources Section
  resources: {
    title: 'Recursos para Culto Jovem',
    subtitle: 'Materiais e recursos para download para líderes e equipes',
    download: 'Baixar',
    downloadLabel: 'Baixar',
    comingSoon: 'Em breve',
    
    // Placeholder resources
    placeholders: {
      guides: {
        title: 'Guias do Jogo',
        description: 'Instruções completas e guias passo a passo para facilitar o jogo em seu grupo.',
      },
      presentations: {
        title: 'Apresentações PPT',
        description: 'Apresentações prontas para usar em suas reuniões e atividades em grupo.',
      },
      additionalMaterial: {
        title: 'Material Adicional',
        description: 'PDFs, planilhas e outros recursos complementares para enriquecer a experiência.',
      },
    },
  },

  // Links of Interest Section
  links: {
    title: 'Links de Interesse',
    subtitle: 'Recursos e sites úteis para a comunidade adventista',
    visit: 'Visitar',
    visitLabel: 'Visitar site',
    
    // Individual links
    items: {
      iasd: {
        title: 'Página IASD',
        description: 'Site oficial da Igreja Adventista do Sétimo Dia',
      },
      noticiasIasd: {
        title: 'Notícias IASD',
        description: 'Portal de notícias adventistas',
      },
      whatsappUu: {
        title: 'Canal WhatsApp UU',
        description: 'Canal oficial do WhatsApp União Uruguaia',
      },
      feliz7play: {
        title: 'Feliz7Play',
        description: 'Plataforma de conteúdo audiovisual adventista',
      },
      videosIasd: {
        title: 'Vídeos IASD',
        description: 'Canal oficial do YouTube da IASD América do Sul',
      },
      radioNt: {
        title: 'Nuevo Tiempo UY',
        description: 'Rádio Nuevo Tiempo Uruguai ao vivo',
      },
      materialesIasd: {
        title: 'Materiais IASD',
        description: 'Centro de downloads de materiais adventistas',
      },
      paraElSabado: {
        title: 'Para o Sábado',
        description: 'Materiais especiais para suas atividades sabáticas',
      },
      galeriaImagenes: {
        title: 'Galeria de Imagens',
        description: 'Galeria de imagens cristãs para uso ministerial',
      },
      leccionJovenes: {
        title: 'Lição Jovens',
        description: 'Lições e materiais para classes de jovens',
      },
      jaMateriales: {
        title: 'Materiais JA',
        description: 'Materiais e informações para Jovens Adventistas',
      },
      accionJoven: {
        title: 'Revista Ação Jovem',
        description: 'Revista trimestral para jovens adventistas',
      },
      conquistadores: {
        title: 'Desbravadores',
        description: 'Materiais e informações do clube de Desbravadores',
      },
      ministerioNino: {
        title: 'Ministério da Criança',
        description: 'Recursos para o ministério infantil',
      },
      recursosBiblicos: {
        title: 'Recursos Bíblicos',
        description: 'Materiais e recursos para o estudo da Bíblia',
      },
      meditaciones2025: {
        title: 'Meditações 2025',
        description: 'Meditações para o pôr do sol 2025',
      },
      seme: {
        title: '7me',
        description: 'Aplicativo oficial para membros e amigos Adventistas',
      },
      probadVed: {
        title: 'Provai e Vede',
        description: 'Vídeos sobre mordomia cristã',
      },
    },
  },

  // Conecta2 Game
  conectados: {
    // Main page
    title: 'Conecta2',
    playing: 'Você está jogando',
    subtitle: 'Jogo de perguntas e respostas',
    selectCategory: 'Selecione uma categoria',
    
    // Access control
    access: {
      title: 'Acesso a Conecta2',
      message: 'Exclusivo para proprietários do jogo.',
      placeholder: 'Insira o código de acesso',
      submit: 'Acessar',
      invalidCode: 'Código de acesso incorreto. Por favor, verifique o código nas instruções do jogo.',
      emptyCode: 'Por favor, insira um código de acesso.',
      success: 'Acesso concedido! Redirecionando...',
      infoTitle: 'Onde encontro o código?',
      infoText: 'O código de acesso está localizado nas instruções incluídas nas instruções do jogo.',
    },
    
    // Game controls
    startGame: 'Iniciar Jogo',
    endGame: 'Terminar Jogo',
    resetGame: 'Reiniciar Jogo',
    newGame: 'Novo Jogo',
    
    // Categories
    categories: {
      sabiduria: {
        title: 'Sabedoria',
        description: 'Teste seu conhecimento em diferentes áreas',
        alt: 'Sabedoria - Teste seu conhecimento de lugares, variedades e personagens',
      },
      alianza: {
        title: 'Aliança',
        description: 'Explore o pacto, a unidade e a coragem',
        alt: 'Aliança - Explore temas de pacto, unidade e coragem',
      },
    },

    // Subcategories
    subcategories: {
      // Sabiduria
      lugares: {
        title: 'Lugares',
        description: 'Lugares e localizações bíblicas',
      },
      variedades: {
        title: 'Variedades',
        description: 'Diferentes aspectos da fé',
      },
      personajes: {
        title: 'Personagens',
        description: 'Personagens e figuras bíblicas',
      },
      
      // Alianza
      'si-nosotros': {
        title: 'Se Nós',
        description: 'Nosso papel no pacto de Deus',
      },
      coincidimos: {
        title: 'Concordamos',
        description: 'Encontrar pontos em comum na fé',
      },
      arriesgamos: {
        title: 'Arriscamos',
        description: 'Tomar riscos por nossas crenças',
      },
    },

    // Question page
    question: {
      title: 'Pergunta',
      question: 'Pergunta',
      prompt: 'Instrução',
      answer: 'Resposta',
      options: 'Opções de resposta:',
      whichVerse: 'Qual versículo você usaria?',
      reference: 'Referência bíblica:',
      showAnswer: 'Mostrar Resposta',
      hideAnswer: 'Ocultar Resposta',
      endTurn: 'Terminar Turno',
      nextQuestion: 'Próxima Pergunta',
      loading: 'Carregando pergunta...',
      noQuestions: 'Não há mais perguntas disponíveis',
      error: 'Erro ao carregar perguntas',
      tryAgain: 'Tentar Novamente',
      trueOption: 'Verdadeiro',
      falseOption: 'Falso',
    },

    // Stats
    stats: {
      questionsAnswered: 'Perguntas respondidas:',
      totalQuestions: 'Total de perguntas:',
      questionsRemaining: 'Perguntas restantes:',
      resetQuestions: 'Reiniciar Perguntas',
      categoryStats: 'Estatísticas desta subcategoria',
    },

    // Completion
    completion: {
      congratulations: 'Parabéns!',
      allQuestionsCompleted: 'Você respondeu todas as perguntas desta categoria!',
      questionsCompleted: 'Perguntas completadas:',
      returnToConectados: 'Voltar para Conecta2',
      returnToCategories: 'Voltar para Categorias',
    },

    // Footer
    footer: {
      quote: '"É melhor ser dois do que um, porque ambos podem ajudar-se mutuamente a alcançar o sucesso."',
      reference: 'Eclesiastes 4:9 (NVT)',
    },

    // PDF Modal (How to Play)
    pdfModal: {
      title: 'Como Jogar',
      mobileTitle: 'Ver Instruções PDF',
      mobileDescription: 'Para uma melhor experiência em dispositivos móveis, você pode abrir o PDF em uma nova aba ou baixá-lo.',
      openPDF: 'Abrir PDF',
      downloadPDF: 'Baixar PDF',
    },
  },

  // Error messages
  errors: {
    general: 'Ocorreu um erro',
    notFound: 'Não encontrado',
    categoryNotFound: 'Categoria não encontrada',
    subcategoryNotFound: 'Subcategoria não encontrada',
    questionsNotFound: 'Nenhuma pergunta encontrada',
    loadingFailed: 'Erro ao carregar',
    networkError: 'Erro de conexão',
  },

  // Success messages
  success: {
    saved: 'Salvo com sucesso',
    deleted: 'Excluído com sucesso',
    updated: 'Atualizado com sucesso',
  },

  // Time and dates
  time: {
    seconds: 'segundos',
    minutes: 'minutos',
    hours: 'horas',
    days: 'dias',
    weeks: 'semanas',
    months: 'meses',
    years: 'anos',
  },

  // Accessibility
  a11y: {
    menu: 'Menu',
    close: 'Fechar',
    open: 'Abrir',
    previous: 'Anterior',
    next: 'Próximo',
    loading: 'Carregando',
  },
};

export default pt;
